import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import slugify from "slugify";
import { toast } from "sonner";
import { Save, ArrowLeft, Eye, Upload, Link as LinkIcon, ExternalLink, Trash2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Table as TableIcon, CalendarClock } from "lucide-react";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";
import { runSeoChecks } from "../../lib/seoChecks";

const CATEGORIES = ["SEO", "Link Building", "Digital PR", "Local SEO", "Content Marketing", "Technical SEO"];

const empty = {
  title: "",
  slug: "",
  excerpt: "",
  body_html: "",
  category: "SEO",
  tags: [],
  cover_image_url: "",
  author: "Zenith Ranking Editorial",
  status: "draft",
  scheduled_for: null,
  focus_keyphrase: "",
  meta_title: "",
  meta_description: "",
  canonical_url: "",
  og_image: "",
  robots: "index,follow",
};

const quillModules = {
  toolbar: {
    container: [
      [{ header: [2, 3, 4, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }, "blockquote", "code-block"],
      ["link", "image"],
      [{ align: [] }],
      ["clean"],
    ],
  },
  table: true,
  clipboard: { matchVisual: false },
};

const quillFormats = ["header", "bold", "italic", "underline", "strike", "list", "blockquote", "code-block", "link", "image", "align", "table"];

// datetime-local <-> ISO-8601 UTC helpers (picker shows the admin's local time)
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function CheckRow({ level, msg }) {
  const Icon = level === "good" ? CheckCircle2 : level === "warn" ? AlertTriangle : XCircle;
  const color = level === "good" ? "text-emerald-600" : level === "warn" ? "text-amber-600" : "text-red-600";
  return (
    <li className="flex items-start gap-2 text-sm py-1.5">
      <Icon size={16} className={`${color} mt-0.5 flex-shrink-0`} />
      <span className="text-ink/85">{msg}</span>
    </li>
  );
}

export default function AdminBlogEditor() {
  const { slug: routeSlug } = useParams();
  const isEdit = !!routeSlug;
  const nav = useNavigate();

  const [form, setForm] = useState(empty);
  const [initialSlug, setInitialSlug] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [tagsInput, setTagsInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [imgUrlInput, setImgUrlInput] = useState("");
  const coverFileRef = useRef(null);
  const inlineFileRef = useRef(null);
  const quillRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/blog/${routeSlug}`);
        setForm({ ...empty, ...data, tags: data.tags || [] });
        setInitialSlug(data.slug);
        setSlugTouched(true);
        setTagsInput((data.tags || []).join(", "));
      } catch {
        toast.error("Could not load post");
        nav("/admin/blog");
      } finally { setLoading(false); }
    })();
  }, [routeSlug, isEdit, nav]);

  // auto-derive slug from title until user manually edits it
  useEffect(() => {
    if (!slugTouched && form.title) {
      setForm((f) => ({ ...f, slug: slugify(form.title, { lower: true, strict: true }) }));
    }
  }, [form.title, slugTouched]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const seo = useMemo(() => runSeoChecks({
    title: form.title,
    slug: form.slug,
    metaTitle: form.meta_title,
    metaDescription: form.meta_description,
    focusKeyphrase: form.focus_keyphrase,
    excerpt: form.excerpt,
    bodyHtml: form.body_html,
    coverImageUrl: form.cover_image_url,
  }), [form]);

  const seoScore = useMemo(() => {
    const total = seo.stats.good + seo.stats.warn + seo.stats.bad;
    if (!total) return 0;
    return Math.round((seo.stats.good / total) * 100);
  }, [seo]);

  const scoreTone = seoScore >= 80 ? "good" : seoScore >= 50 ? "warn" : "bad";
  const scoreDot = scoreTone === "good" ? "bg-emerald-500" : scoreTone === "warn" ? "bg-amber-500" : "bg-red-500";

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/blog/upload-image", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  };

  const handleCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      update("cover_image_url", url);
      if (!form.og_image) update("og_image", url);
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const applyCoverUrl = () => {
    if (!imgUrlInput.trim()) return;
    update("cover_image_url", imgUrlInput.trim());
    if (!form.og_image) update("og_image", imgUrlInput.trim());
    setImgUrlInput("");
    toast.success("Cover image set");
  };

  const handleInlineImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      const editor = quillRef.current?.getEditor?.();
      if (editor) {
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, "image", url, "user");
        editor.setSelection(range.index + 1, 0);
      }
      toast.success("Image inserted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (nextStatus) => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.body_html || form.body_html === "<p><br></p>") return toast.error("Post body is empty");
    const status = nextStatus || form.status;
    const scheduledIso = form.scheduled_for || null;
    if (status === "scheduled") {
      if (!scheduledIso) return toast.error("Pick a date and time to schedule this post");
      if (new Date(scheduledIso).getTime() <= Date.now()) {
        return toast.error("Scheduled time must be in the future");
      }
    }
    const payload = {
      ...form,
      status,
      scheduled_for: status === "scheduled" ? scheduledIso : null,
      tags: (tagsInput || "").split(",").map((t) => t.trim()).filter(Boolean),
      slug: form.slug ? slugify(form.slug, { lower: true, strict: true }) : undefined,
    };
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`/admin/blog/${initialSlug}`, payload);
        toast.success(
          status === "published" ? "Published!" : status === "scheduled" ? "Scheduled!" : "Saved"
        );
        if (data.slug && data.slug !== initialSlug) {
          nav(`/admin/blog/edit/${data.slug}`, { replace: true });
          setInitialSlug(data.slug);
        }
        setForm({ ...empty, ...data, tags: data.tags || [] });
      } else {
        const { data } = await api.post(`/admin/blog`, payload);
        toast.success(
          status === "published" ? "Published!" : status === "scheduled" ? "Scheduled!" : "Saved as draft"
        );
        nav(`/admin/blog/edit/${data.slug}`, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const tableAction = (fn) => {
    const editor = quillRef.current?.getEditor?.();
    const table = editor?.getModule?.("table");
    if (!editor || !table) return toast.error("Editor not ready");
    editor.focus();
    const range = editor.getSelection(true);
    if (!range) {
      editor.setSelection(editor.getLength() - 1, 0);
    }
    try {
      fn(table, editor);
    } catch {
      toast.error("Place the cursor inside a table first");
    }
  };

  const insertTable = () => {
    const rows = Math.min(20, Math.max(1, parseInt(window.prompt("How many rows? (including the header row)", "3") || "0", 10)));
    const cols = Math.min(8, Math.max(1, parseInt(window.prompt("How many columns?", "3") || "0", 10)));
    if (!rows || !cols) return;
    tableAction((table, editor) => {
      // Quill turns the current line into the first cell, so always start the
      // table on a fresh empty line — otherwise the paragraph you were writing
      // gets swallowed into cell 1.
      const range = editor.getSelection(true) || { index: Math.max(0, editor.getLength() - 1), length: 0 };
      const [line] = editor.getLine(range.index);
      const lineIsEmpty = !line || line.length() <= 1;
      let at = range.index;
      if (!lineIsEmpty) {
        editor.insertText(range.index, "\n", "user");
        at = range.index + 1;
      }
      editor.setSelection(at, 0, "silent");
      table.insertTable(rows, cols);
      toast.success(`${rows} × ${cols} table inserted — the first row is the header`);
    });
  };

  if (loading) return <AdminLayout><div className="text-inkSoft text-sm">Loading…</div></AdminLayout>;

  return (
    <AdminLayout>
      {/* header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <Link to="/admin/blog" className="text-xs text-inkSoft hover:text-ink inline-flex items-center gap-1"><ArrowLeft size={12} /> Back to posts</Link>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-2">{isEdit ? "Edit post" : "New post"}</h1>
        </div>
        <div className="flex items-center gap-2">
          {isEdit && form.status === "published" && (
            <a href={`/blog/${initialSlug}`} target="_blank" rel="noreferrer" className="btn-secondary text-sm" data-testid="admin-blog-view">
              <ExternalLink size={14} /> View
            </a>
          )}
          <button onClick={() => save("draft")} disabled={saving} className="btn-secondary text-sm" data-testid="admin-blog-save-draft">
            <Save size={14} /> {saving ? "Saving…" : "Save draft"}
          </button>
          {form.status === "scheduled" && (
            <button onClick={() => save("scheduled")} disabled={saving} className="btn-secondary text-sm" data-testid="admin-blog-schedule">
              <CalendarClock size={14} /> {saving ? "Saving…" : "Schedule"}
            </button>
          )}
          <button onClick={() => save("published")} disabled={saving} className="btn-primary text-sm" data-testid="admin-blog-publish">
            <Eye size={14} /> {form.status === "published" ? "Update" : "Publish now"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        {/* MAIN */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-line bg-white p-5">
            <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">Title</label>
            <input
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="How we grew a SaaS to 100k organic visits…"
              className="w-full text-2xl font-display font-semibold tracking-tight outline-none py-2 bg-transparent"
              data-testid="admin-blog-title"
            />
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-line">
              <span className="text-xs text-inkSoft">URL:</span>
              <span className="text-xs text-inkSoft font-mono">/blog/</span>
              <input
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); update("slug", e.target.value); }}
                placeholder="post-slug"
                className="flex-1 text-xs font-mono outline-none bg-transparent border-b border-transparent focus:border-line"
                data-testid="admin-blog-slug"
              />
              <button onClick={() => { setSlugTouched(false); update("slug", slugify(form.title || "", { lower: true, strict: true })); }} className="text-xs text-inkSoft hover:text-ink inline-flex items-center gap-1" title="Regenerate from title">
                <RefreshCw size={12} />
              </button>
            </div>
          </div>

          {/* Cover image */}
          <div className="rounded-2xl border border-line bg-white p-5">
            <label className="block text-xs uppercase tracking-widest text-inkSoft mb-3">Cover image</label>
            {form.cover_image_url ? (
              <div className="relative rounded-xl overflow-hidden border border-line aspect-[16/8] bg-surfaceAlt">
                <img src={form.cover_image_url} alt="cover" className="w-full h-full object-cover" data-testid="admin-blog-cover-preview" />
                <button
                  onClick={() => update("cover_image_url", "")}
                  className="absolute top-3 right-3 bg-white/95 hover:bg-white rounded-lg h-9 w-9 grid place-items-center border border-line"
                  title="Remove cover"
                >
                  <Trash2 size={14} className="text-red-600" />
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => coverFileRef.current?.click()}
                  className="w-full border-2 border-dashed border-line rounded-xl py-10 flex flex-col items-center gap-2 text-inkSoft hover:border-ink hover:text-ink transition"
                  data-testid="admin-blog-cover-upload"
                >
                  <Upload size={20} />
                  <span className="text-sm font-medium">{uploading ? "Uploading…" : "Upload cover image"}</span>
                  <span className="text-xs">JPG, PNG or WebP · up to 8MB</span>
                </button>
                <div className="flex items-center gap-2">
                  <LinkIcon size={14} className="text-inkSoft" />
                  <input
                    value={imgUrlInput}
                    onChange={(e) => setImgUrlInput(e.target.value)}
                    placeholder="…or paste an image URL"
                    className="flex-1 border border-line rounded-lg px-3 py-2 text-sm"
                    data-testid="admin-blog-cover-url-input"
                  />
                  <button onClick={applyCoverUrl} className="btn-secondary text-xs" data-testid="admin-blog-cover-url-apply">Use URL</button>
                </div>
              </div>
            )}
            <input ref={coverFileRef} type="file" accept="image/*" hidden onChange={handleCoverFile} />
          </div>

          {/* Excerpt */}
          <div className="rounded-2xl border border-line bg-white p-5">
            <label className="block text-xs uppercase tracking-widest text-inkSoft mb-2">Excerpt <span className="text-inkSoft/60 normal-case">(shown on card + fallback meta description)</span></label>
            <textarea
              value={form.excerpt}
              onChange={(e) => update("excerpt", e.target.value)}
              rows={2}
              className="w-full border border-line rounded-lg px-3 py-2 text-sm outline-none focus:border-ink"
              data-testid="admin-blog-excerpt"
            />
          </div>

          {/* Body editor */}
          <div className="rounded-2xl border border-line bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <label className="block text-xs uppercase tracking-widest text-inkSoft">Post body</label>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={insertTable}
                  className="text-xs text-terra hover:underline inline-flex items-center gap-1"
                  data-testid="admin-blog-insert-table"
                  type="button"
                >
                  <TableIcon size={12} /> Insert table
                </button>
                <button
                  onClick={() => inlineFileRef.current?.click()}
                  className="text-xs text-terra hover:underline inline-flex items-center gap-1"
                  data-testid="admin-blog-inline-image"
                  type="button"
                >
                  <Upload size={12} /> Insert image
                </button>
              </div>
              <input ref={inlineFileRef} type="file" accept="image/*" hidden onChange={handleInlineImage} />
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mb-3 pb-3 border-b border-line" data-testid="admin-blog-table-tools">
              <span className="text-[11px] uppercase tracking-widest text-inkSoft mr-1">Table</span>
              <button type="button" className="table-tool" data-testid="table-row-above" onClick={() => tableAction((t) => t.insertRowAbove())}>+ Row above</button>
              <button type="button" className="table-tool" data-testid="table-row-below" onClick={() => tableAction((t) => t.insertRowBelow())}>+ Row below</button>
              <button type="button" className="table-tool" data-testid="table-col-left" onClick={() => tableAction((t) => t.insertColumnLeft())}>+ Col left</button>
              <button type="button" className="table-tool" data-testid="table-col-right" onClick={() => tableAction((t) => t.insertColumnRight())}>+ Col right</button>
              <button type="button" className="table-tool table-tool-danger" data-testid="table-del-row" onClick={() => tableAction((t) => t.deleteRow())}>− Row</button>
              <button type="button" className="table-tool table-tool-danger" data-testid="table-del-col" onClick={() => tableAction((t) => t.deleteColumn())}>− Column</button>
              <button type="button" className="table-tool table-tool-danger" data-testid="table-del-table" onClick={() => tableAction((t) => t.deleteTable())}>Delete table</button>
            </div>
            <div className="quill-wrap" data-testid="admin-blog-body">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={form.body_html}
                onChange={(v) => update("body_html", v)}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write your article… use H2/H3 for structure, add internal links to /services and /case-studies for SEO."
              />
            </div>
          </div>
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-6">
          {/* SEO score card */}
          <div className="rounded-2xl border border-line bg-white p-5" data-testid="admin-blog-seo-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="eyebrow">Yoast-style SEO</div>
                <div className="font-display text-2xl font-bold tracking-tight mt-1">Score {seoScore}</div>
              </div>
              <div className={`h-12 w-12 rounded-full ${scoreDot} grid place-items-center text-white font-bold`}>{seoScore}</div>
            </div>

            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {seo.stats.good} good</div>
              <div className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> {seo.stats.warn} warn</div>
              <div className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" /> {seo.stats.bad} problem</div>
            </div>

            <div className="mt-4 rounded-xl bg-surfaceAlt/40 p-3">
              <div className="text-[11px] uppercase tracking-widest text-inkSoft mb-1">Google preview</div>
              <div className="text-[13px] text-terra truncate">{`zenithranking.com/blog/${form.slug || "your-slug"}`}</div>
              <div className="text-[15px] text-blue-800 leading-snug mt-0.5 line-clamp-1">{form.meta_title || form.title || "Meta title preview"}</div>
              <div className="text-[13px] text-inkSoft leading-snug line-clamp-2 mt-0.5">{form.meta_description || form.excerpt || "Meta description preview…"}</div>
            </div>

            <ul className="mt-4 divide-y divide-line/60" data-testid="admin-blog-seo-checks">
              {seo.checks.map((c, i) => <CheckRow key={i} {...c} />)}
            </ul>
          </div>

          {/* Publish/settings */}
          <div className="rounded-2xl border border-line bg-white p-5 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => {
                  const v = e.target.value;
                  update("status", v);
                  if (v !== "scheduled") update("scheduled_for", null);
                }}
                className="w-full border border-line rounded-lg px-3 py-2 text-sm"
                data-testid="admin-blog-status"
              >
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
              </select>
            </div>
            {form.status === "scheduled" && (
              <div data-testid="admin-blog-schedule-block">
                <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">
                  Publish on <span className="text-inkSoft/60 normal-case">(your local time)</span>
                </label>
                <input
                  type="datetime-local"
                  value={isoToLocalInput(form.scheduled_for)}
                  onChange={(e) => update("scheduled_for", localInputToIso(e.target.value))}
                  className="w-full border border-line rounded-lg px-3 py-2 text-sm"
                  data-testid="admin-blog-scheduled-for"
                />
                <p className="text-[11px] text-inkSoft mt-1.5">
                  {form.scheduled_for
                    ? `Goes live automatically on ${new Date(form.scheduled_for).toLocaleString()} — hidden from the blog, search engines and the sitemap until then.`
                    : "Pick the date and time this post should go live."}
                </p>
              </div>
            )}
            <div>
              <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                className="w-full border border-line rounded-lg px-3 py-2 text-sm"
                data-testid="admin-blog-category"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">Tags <span className="text-inkSoft/60 normal-case">(comma-separated)</span></label>
              <input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                className="w-full border border-line rounded-lg px-3 py-2 text-sm"
                placeholder="seo, guest posting, 2026"
                data-testid="admin-blog-tags"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">Author</label>
              <input
                value={form.author}
                onChange={(e) => update("author", e.target.value)}
                className="w-full border border-line rounded-lg px-3 py-2 text-sm"
                data-testid="admin-blog-author"
              />
            </div>
          </div>

          {/* Advanced SEO */}
          <div className="rounded-2xl border border-line bg-white p-5 space-y-4">
            <div className="eyebrow">Advanced SEO</div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">Focus keyphrase</label>
              <input
                value={form.focus_keyphrase}
                onChange={(e) => update("focus_keyphrase", e.target.value)}
                className="w-full border border-line rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. local seo audit"
                data-testid="admin-blog-focus"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">Meta title <span className="text-inkSoft/60 normal-case">({(form.meta_title || "").length}/60)</span></label>
              <input
                value={form.meta_title}
                onChange={(e) => update("meta_title", e.target.value)}
                className="w-full border border-line rounded-lg px-3 py-2 text-sm"
                placeholder="Falls back to title"
                data-testid="admin-blog-meta-title"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">Meta description <span className="text-inkSoft/60 normal-case">({(form.meta_description || "").length}/160)</span></label>
              <textarea
                value={form.meta_description}
                onChange={(e) => update("meta_description", e.target.value)}
                rows={3}
                className="w-full border border-line rounded-lg px-3 py-2 text-sm"
                data-testid="admin-blog-meta-desc"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">Canonical URL</label>
              <input
                value={form.canonical_url}
                onChange={(e) => update("canonical_url", e.target.value)}
                className="w-full border border-line rounded-lg px-3 py-2 text-sm"
                placeholder="Leave blank for default"
                data-testid="admin-blog-canonical"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">OG image URL</label>
              <input
                value={form.og_image}
                onChange={(e) => update("og_image", e.target.value)}
                className="w-full border border-line rounded-lg px-3 py-2 text-sm"
                placeholder="Defaults to cover image"
                data-testid="admin-blog-og"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-inkSoft mb-1">Robots</label>
              <select
                value={form.robots}
                onChange={(e) => update("robots", e.target.value)}
                className="w-full border border-line rounded-lg px-3 py-2 text-sm"
                data-testid="admin-blog-robots"
              >
                <option value="index,follow">index, follow</option>
                <option value="noindex,follow">noindex, follow</option>
                <option value="index,nofollow">index, nofollow</option>
                <option value="noindex,nofollow">noindex, nofollow</option>
              </select>
            </div>
          </div>
        </aside>
      </div>

      <style>{`
        .quill-wrap .ql-toolbar { border-radius: 12px 12px 0 0; border-color: var(--line); }
        .quill-wrap .ql-container { border-radius: 0 0 12px 12px; border-color: var(--line); min-height: 380px; font-size: 15px; font-family: inherit; }
        .quill-wrap .ql-editor { min-height: 380px; line-height: 1.75; }
        .quill-wrap .ql-editor h2 { font-family: 'Cabinet Grotesk', ui-serif; font-weight: 700; font-size: 1.5rem; margin-top: 1.25rem; }
        .quill-wrap .ql-editor h3 { font-family: 'Cabinet Grotesk', ui-serif; font-weight: 700; font-size: 1.2rem; margin-top: 1rem; }
        .quill-wrap .ql-editor a { color: var(--terra); text-decoration: underline; }
        .quill-wrap .ql-editor img { border-radius: 12px; margin: 12px 0; max-width: 100%; }
        .quill-wrap .ql-editor table { width: 100%; border-collapse: collapse; margin: 14px 0; table-layout: fixed; }
        .quill-wrap .ql-editor td { border: 1px solid var(--line); padding: 8px 10px; vertical-align: top; }
        .quill-wrap .ql-editor tr:first-child td { background: var(--surface-alt); font-weight: 700; }
        .table-tool {
          font-size: 11px; padding: 4px 9px; border-radius: 999px;
          border: 1px solid var(--line); color: var(--ink-soft); background: transparent;
          transition: border-color .18s ease, color .18s ease, background-color .18s ease;
        }
        .table-tool:hover { border-color: var(--terra); color: var(--terra); }
        .table-tool-danger:hover { border-color: #dc2626; color: #dc2626; }
      `}</style>
    </AdminLayout>
  );
}
